import { NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useEffect } from 'react'
import { cn } from '@/lib/utils'
import { useSidebarCollapsed, useMobileSidebarOpen, useAppStore, useUser } from '@/store'
import { authApi } from '@/lib/api'
import { useStaggerEntrance } from '@/hooks/useStaggerEntrance'
import {
  LayoutDashboard,
  Star,
  Calendar,
  Search,
  Heart,
  TrendingUp,
  Sparkles,
  Settings,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Code2,
  Clock,
  CreditCard,
  ShieldAlert,
} from 'lucide-react'
import { Tooltip } from '@/components/Tooltip'

const NAV_ITEMS = [
  {
    group: 'Core',
    items: [
      { to: '/dashboard',     icon: LayoutDashboard, label: 'Dashboard',      sanskrit: 'मुख्य' },
      { to: '/daily-timings', icon: Clock,            label: 'Daily Timings',  sanskrit: 'चौघड़िया' },
      { to: '/birth-chart',   icon: Star,             label: 'Birth Chart',    sanskrit: 'जन्म' },
      { to: '/dasha',         icon: TrendingUp,       label: 'Dasha Timeline', sanskrit: 'दशा' },
    ],
  },
  {
    group: 'Guidance',
    items: [
      { to: '/muhurta',       icon: Calendar, label: 'Muhurta',       sanskrit: 'मुहूर्त' },
      { to: '/event-muhurta', icon: Search,   label: 'Event Finder',  sanskrit: 'शुभ' },
      { to: '/compatibility', icon: Heart,    label: 'Kundali Match', sanskrit: 'कुंडली' },
      { to: '/insights',      icon: Sparkles, label: 'Influences',    sanskrit: 'ग्रह' },
      { to: '/remedies',      icon: Star,     label: 'Remedies',      sanskrit: 'उपाय' },
    ],
  },
  {
    group: 'Developer',
    items: [
      { to: '/developer', icon: Code2, label: 'API Dashboard', sanskrit: 'API' },
    ],
  },
]

export function Sidebar() {
  const collapsed = useSidebarCollapsed()
  const mobileOpen = useMobileSidebarOpen()
  const { toggleSidebar, closeMobileSidebar, logout } = useAppStore()
  const user = useUser()
  const navigate = useNavigate()
  const location = useLocation()
  const isAdmin = user?.role?.toUpperCase() === 'ADMIN'
  const navRef = useStaggerEntrance<HTMLElement>({ selector: 'li', stagger: 0.035, duration: 0.3, y: 6 })

  // Close mobile sidebar on route change
  useEffect(() => {
    closeMobileSidebar()
  }, [location.pathname]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = async () => {
    try {
      await authApi.logout()
    } finally {
      logout()
      navigate('/login', { replace: true })
    }
  }

  const handleNavClick = () => {
    closeMobileSidebar()
  }

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/70 backdrop-blur-[2px] md:hidden"
          onClick={closeMobileSidebar}
          aria-hidden="true"
        />
      )}

      <aside
        className={cn(
          'fixed left-0 top-0 h-full z-40 flex flex-col',
          // Glass surface — slightly deeper than before for contrast
          'bg-cosmic-nebula/60 backdrop-blur-2xl border-r border-white/[0.06]',
          // Smooth transition for width + slide using ease-cosmic
          'transition-[width,transform] duration-300 ease-cosmic',
          // Desktop: always visible, width controlled by collapsed
          'md:translate-x-0',
          collapsed ? 'md:w-[60px]' : 'md:w-[var(--sidebar-width)]',
          // Mobile: full-width sidebar slides in from left
          mobileOpen ? 'translate-x-0 w-[var(--sidebar-width)]' : '-translate-x-full w-[var(--sidebar-width)]',
        )}
      >
        {/* Subtle top gradient accent */}
        <div
          className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-violet/30 to-transparent pointer-events-none"
          aria-hidden="true"
        />

        {/* ── Logo / brand ──────────────────────────────────────────────── */}
        <div
          className={cn(
            'flex items-center h-[var(--header-height)] px-4 border-b border-white/[0.06] flex-shrink-0',
            collapsed ? 'md:justify-center md:px-0' : 'gap-3',
          )}
        >
          <div className="relative flex-shrink-0">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-violet to-violet-mid flex items-center justify-center shadow-glow-violet">
              <span className="text-gold text-sm font-display font-semibold">✦</span>
            </div>
          </div>
          <div
            className={cn(
              'min-w-0 overflow-hidden transition-[width,opacity] duration-300 ease-cosmic',
              collapsed ? 'md:w-0 md:opacity-0' : 'w-auto opacity-100',
            )}
          >
            <p className="text-star-white font-semibold text-sm leading-none tracking-tight whitespace-nowrap">Shubh Time</p>
            <p className="text-star-dim text-2xs font-mono mt-0.5">शुभ समय</p>
          </div>
        </div>

        {/* ── Navigation ────────────────────────────────────────────────── */}
        <nav ref={navRef} className="flex-1 overflow-y-auto overflow-x-hidden py-3 px-2 space-y-4 scrollbar-none">
          {NAV_ITEMS.map((group) => (
            <div key={group.group}>
              {/* Group header */}
              <div
                className={cn(
                  'flex items-center gap-2 mb-1.5 px-2 overflow-hidden transition-[height,opacity] duration-300 ease-cosmic',
                  collapsed ? 'md:h-0 md:opacity-0 md:mb-0' : 'h-auto opacity-100',
                )}
              >
                <span className="w-1 h-1 rounded-full bg-gold/40 flex-shrink-0" aria-hidden="true" />
                <p className="text-2xs font-mono uppercase tracking-widest text-star-dim truncate">
                  {group.group}
                </p>
              </div>

              {/* Separator dot when collapsed */}
              {collapsed && (
                <div className="hidden md:flex justify-center mb-2" aria-hidden="true">
                  <span className="w-3 h-px bg-white/10 rounded-full" />
                </div>
              )}

              <ul className="space-y-px">
                {group.items.map((item) => (
                  <li key={item.to}>
                    <Tooltip
                      content={`${item.label}  ${item.sanskrit}`}
                      side="right"
                      delayDuration={200}
                      disabled={!collapsed}
                    >
                      <NavLink
                        to={item.to}
                        onClick={handleNavClick}
                        className={({ isActive }) =>
                          cn(
                            'nav-item group',
                            isActive && 'active',
                            collapsed && 'md:justify-center md:px-0 md:w-full',
                          )
                        }
                      >
                        {({ isActive }) => (
                          <>
                            <item.icon
                              className={cn(
                                'w-4 h-4 flex-shrink-0 transition-colors duration-150',
                                isActive ? 'text-gold' : 'text-star-muted group-hover:text-star-soft',
                              )}
                            />
                            <span
                              className={cn(
                                'flex-1 flex items-baseline gap-1.5 min-w-0 overflow-hidden transition-[width,opacity] duration-300 ease-cosmic',
                                collapsed ? 'md:w-0 md:opacity-0' : 'w-auto opacity-100',
                              )}
                            >
                              <span className="truncate whitespace-nowrap">{item.label}</span>
                              <span
                                className={cn(
                                  'text-2xs font-display shrink-0 transition-colors duration-150',
                                  isActive ? 'text-gold/50' : 'text-star-dim',
                                )}
                              >
                                {item.sanskrit}
                              </span>
                            </span>
                          </>
                        )}
                      </NavLink>
                    </Tooltip>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        {/* ── Bottom actions ─────────────────────────────────────────────── */}
        <div className="border-t border-white/[0.06] py-2.5 px-2 space-y-px flex-shrink-0">
          {isAdmin && (
            <Tooltip content="Admin Panel" side="right" delayDuration={200} disabled={!collapsed}>
              <NavLink
                to="/admin/users"
                onClick={handleNavClick}
                className={({ isActive }) =>
                  cn('nav-item group', isActive && 'active', collapsed && 'md:justify-center md:px-0 md:w-full')
                }
              >
                {({ isActive }) => (
                  <>
                    <ShieldAlert className={cn('w-4 h-4 flex-shrink-0', isActive ? 'text-gold' : 'text-gold-dim/70')} />
                    <span
                      className={cn(
                        'overflow-hidden transition-[width,opacity] duration-300 ease-cosmic whitespace-nowrap',
                        collapsed ? 'md:w-0 md:opacity-0' : 'w-auto opacity-100',
                      )}
                    >
                      Admin
                    </span>
                  </>
                )}
              </NavLink>
            </Tooltip>
          )}

          <Tooltip content="Billing" side="right" delayDuration={200} disabled={!collapsed}>
            <NavLink
              to="/billing"
              onClick={handleNavClick}
              className={({ isActive }) =>
                cn('nav-item group', isActive && 'active', collapsed && 'md:justify-center md:px-0 md:w-full')
              }
            >
              {({ isActive }) => (
                <>
                  <CreditCard className={cn('w-4 h-4 flex-shrink-0 transition-colors duration-150', isActive ? 'text-gold' : 'text-star-muted group-hover:text-star-soft')} />
                  <span
                    className={cn(
                      'overflow-hidden transition-[width,opacity] duration-300 ease-cosmic whitespace-nowrap',
                      collapsed ? 'md:w-0 md:opacity-0' : 'w-auto opacity-100',
                    )}
                  >
                    Billing
                  </span>
                </>
              )}
            </NavLink>
          </Tooltip>

          <Tooltip content="Settings" side="right" delayDuration={200} disabled={!collapsed}>
            <NavLink
              to="/settings"
              onClick={handleNavClick}
              className={({ isActive }) =>
                cn('nav-item group', isActive && 'active', collapsed && 'md:justify-center md:px-0 md:w-full')
              }
            >
              {({ isActive }) => (
                <>
                  <Settings className={cn('w-4 h-4 flex-shrink-0 transition-colors duration-150', isActive ? 'text-gold' : 'text-star-muted group-hover:text-star-soft')} />
                  <span
                    className={cn(
                      'overflow-hidden transition-[width,opacity] duration-300 ease-cosmic whitespace-nowrap',
                      collapsed ? 'md:w-0 md:opacity-0' : 'w-auto opacity-100',
                    )}
                  >
                    Settings
                  </span>
                </>
              )}
            </NavLink>
          </Tooltip>

          <Tooltip content="Sign out" side="right" delayDuration={200} disabled={!collapsed}>
            <button
              onClick={handleLogout}
              className={cn(
                'nav-item w-full text-left group',
                'hover:bg-inauspicious/10 hover:text-inauspicious',
                collapsed && 'md:justify-center md:px-0',
              )}
            >
              <LogOut className="w-4 h-4 flex-shrink-0 text-star-muted group-hover:text-inauspicious transition-colors duration-150" />
              <span
                className={cn(
                  'overflow-hidden transition-[width,opacity] duration-300 ease-cosmic whitespace-nowrap',
                  collapsed ? 'md:w-0 md:opacity-0' : 'w-auto opacity-100',
                )}
              >
                Sign out
              </span>
            </button>
          </Tooltip>
        </div>

        {/* ── Collapse toggle (desktop only) ────────────────────────────── */}
        <button
          onClick={toggleSidebar}
          className={cn(
            'absolute -right-3 top-[72px] hidden md:flex',
            'w-6 h-6 rounded-full items-center justify-center',
            'bg-cosmic-dusk border border-white/10 text-star-muted',
            'hover:bg-cosmic-aurora hover:text-star-white hover:border-violet/30',
            'transition-all duration-200 ease-cosmic shadow-card',
            'group',
          )}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <span className="transition-transform duration-200 ease-cosmic">
            {collapsed
              ? <ChevronRight className="w-3.5 h-3.5" />
              : <ChevronLeft className="w-3.5 h-3.5" />
            }
          </span>
        </button>
      </aside>
    </>
  )
}
